import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Phone, CalendarDays } from "lucide-react";
import { BRAND } from "../lib/brand";
import { useT } from "../lib/i18n";

const HERO_VIDEO =
  "https://videos.pexels.com/video-files/35307378/14958783_2560_1440_30fps.mp4";
const HERO_VIDEO_ALT =
  "https://videos.pexels.com/video-files/10500087/10500087-hd_1920_1080_30fps.mp4";
const HERO_POSTER =
  "https://images.pexels.com/videos/35307378/4x4-4x4-vehicle-canam-cinematic-35307378.jpeg?auto=compress&cs=tinysrgb&w=1920";

const TYPE_WORDS = ["EXPLORE", "SHRED", "CONQUER", "OWN"];

function useTypewriter(words, typeMs = 90, holdMs = 1400, deleteMs = 40) {
  const [i, setI] = useState(0);
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[i % words.length];
    let t;
    if (!deleting && text !== current) {
      t = setTimeout(() => setText(current.slice(0, text.length + 1)), typeMs);
    } else if (!deleting && text === current) {
      t = setTimeout(() => setDeleting(true), holdMs);
    } else if (deleting && text !== "") {
      t = setTimeout(() => setText(current.slice(0, text.length - 1)), deleteMs);
    } else {
      t = setTimeout(() => {
        setDeleting(false);
        setI((n) => (n + 1) % words.length);
      }, 250);
    }
    return () => clearTimeout(t);
  }, [text, deleting, i, words, typeMs, holdMs, deleteMs]);

  return text;
}

export default function Hero({ onOpenBookText }) {
  const { t } = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.6, 0.9]);

  const typed = useTypewriter(t("hero.typedWords"));
  const bodyParts = t("hero.body");

  return (
    <section
      id="top"
      ref={ref}
      className="relative w-full min-h-[100vh] min-h-[100svh] overflow-hidden bg-black"
      data-testid="hero-section"
    >
      {/* Parallax video */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <video
          className="w-full h-full object-cover opacity-70"
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_POSTER}
          preload="auto"
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
          <source src={HERO_VIDEO_ALT} type="video/mp4" />
        </video>
      </motion.div>

      {/* Dark tint */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-10 bg-gradient-to-b from-black/70 via-black/55 to-black"
      />

      {/* Pixel grid decoration — right side */}
      <div className="absolute inset-y-0 right-0 w-[45%] z-10 pointer-events-none pixel-dots opacity-40 mix-blend-screen" />

      {/* Diagonal red slash */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 1.2 }}
        className="origin-left absolute bottom-[30%] left-6 md:left-10 lg:left-16 h-[2px] w-24 md:w-36 bg-[color:var(--primary)] z-20"
      />

      {/* Grain */}
      <div className="grain absolute inset-0 z-10" />

      {/* Content */}
      <div className="relative z-20 min-h-[100vh] min-h-[100svh] flex flex-col justify-center pt-24 md:pt-32 pb-20 px-6 md:px-10 lg:px-16 max-w-[1600px] mx-auto">
        {/* Top meta strip */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-6 md:mb-8 inline-flex items-center gap-3 self-start border border-white/15 bg-black/50 backdrop-blur px-3 py-1.5"
          data-testid="hero-badge"
        >
          <span className="flex h-1.5 w-1.5 rounded-full bg-[color:var(--primary)] animate-pulse" />
          <span className="font-mono text-[10px] tracking-[0.28em] text-white/80 uppercase">
            {t("hero.badge")}
          </span>
        </motion.div>

        {/* Kinetic heading */}
        <div className="max-w-[1400px]">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="overline mb-4"
            data-testid="hero-overline"
          >
            {t("hero.overline")}
          </motion.p>

          <h1
            className="font-display text-white leading-[0.9] text-[15vw] md:text-[10.5vw] lg:text-[8.5vw]"
            data-testid="hero-headline"
          >
            <span className="block">
              <span className="text-[color:var(--primary)]">{typed}</span>
              <span className="blink-caret align-middle" />
            </span>
            <span className="block">{t("hero.headlineTail")}</span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-8 md:mt-10 grid md:grid-cols-[1.3fr_1fr] gap-6 md:gap-14 items-end"
          >
            <p className="text-white/75 text-sm md:text-base leading-relaxed max-w-xl">
              {bodyParts[0]}
              <strong className="text-white">{bodyParts[1]}</strong>
              {bodyParts[2]}
              <strong className="text-white">{bodyParts[3]}</strong>
              {bodyParts[4]}
              <span className="text-[color:var(--primary)]">{bodyParts[5]}</span>
              {bodyParts[6]}
            </p>

            <div className="flex flex-wrap gap-3">
              <a
                href={BRAND.bookingUrl}
                className="inline-flex items-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-[0.16em] text-[11px] px-4 py-3 transition-colors"
                data-testid="hero-book-now-btn"
              >
                <CalendarDays size={13} /> {t("hero.bookNow")}
              </a>
              <button
                onClick={onOpenBookText}
                className="inline-flex items-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-[0.16em] text-[11px] px-4 py-3 transition-colors"
                data-testid="hero-book-text-btn"
              >
                {t("hero.bookViaText")} <ArrowRight size={14} />
              </button>
              <a
                href={`tel:${BRAND.phoneRaw}`}
                className="inline-flex items-center gap-2 border border-white/20 hover:bg-white hover:text-black text-white font-bold uppercase tracking-[0.16em] text-[11px] px-4 py-3 transition-colors"
                data-testid="hero-call-btn"
              >
                <Phone size={12} /> {BRAND.phone}
              </a>
            </div>
          </motion.div>
        </div>

        {/* Meta row at bottom */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-10 md:mt-14 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/60"
        >
          <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-mono">
            <MapPin size={12} className="text-[color:var(--primary)]" /> Kissimmee · Ocala · Orlando
          </span>
          <span className="hidden md:inline-block h-3 w-px bg-white/20" />
          <span className="text-[10px] uppercase tracking-[0.28em] font-mono">{t("hero.metaTour")}</span>
          <span className="hidden md:inline-block h-3 w-px bg-white/20" />
          <span className="text-[10px] uppercase tracking-[0.28em] font-mono">{t("hero.metaNight")}</span>
          <span className="hidden md:inline-block h-3 w-px bg-white/20" />
          <span className="text-[10px] uppercase tracking-[0.28em] font-mono">{t("hero.metaAges")}</span>
        </motion.div>
      </div>
    </section>
  );
}
